/* eslint-disable import/no-extraneous-dependencies */
import React from 'react'
import Lottie from 'lottie-react'
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  PieLabelRenderProps,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'

import chartLoading from './Animations/chartLoading.json'

export interface PieDataItem {
  name: string
  value: number // Used only for display
}

export interface DispatchPieChartProps {
  pieData: PieDataItem[]
  showPieChart: boolean
  pieError?: string
  pieChartIsLoading?: boolean
}
const COLORS = ['#f97316', '#a5492a']

const renderLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: PieLabelRenderProps) => {
  const radius =
    Number(innerRadius ?? 0) +
    (Number(outerRadius ?? 0) - Number(innerRadius ?? 0)) * 0.5
  const x = Number(cx) + radius * Math.cos(-midAngle * (Math.PI / 180))
  const y = Number(cy) + radius * Math.sin(-midAngle * (Math.PI / 180))

  return (
    <text
      x={x}
      y={y}
      fill='#000'
      textAnchor='middle'
      dominantBaseline='central'
      fontSize={13}
      fontWeight='bold'>
      {((percent ?? 0) * 100).toFixed(0)}%
    </text>
  )
}

export const DispatchPieChart: React.FC<DispatchPieChartProps> = ({
  pieData,
  showPieChart,
  pieError,
  pieChartIsLoading,
}) => {
  return (
    <div className='flex flex-col lg:flex-row items-start justify-between w-full px-4 sm:px-6 py-4 gap-6'>
      {/* Heading Section */}
      <div className='w-full min-w-[200px] max-w-[2000px] bg-white  p-4 mx-auto'>
        <h2 className='text-lg sm:text-xl font-semibold text-N-600 text-center'>
          On-time Dispatch Rate
        </h2>
        <p className='text-xs sm:text-sm text-N-400 text-center mb-4'>
          (Dispatch Orders Only)
        </p>

        {/* Chart Section */}
        {pieChartIsLoading ? (
          <div className='flex justify-center items-center'>
            <Lottie
              animationData={chartLoading}
              loop
              style={{ width: 300, height: 300 }}
            />
          </div>
        ) : (
          <div className='w-full h-[200px] sm:h-[300px]'>
            {showPieChart && (
              <ResponsiveContainer width='100%' height='100%'>
                <PieChart>
                  <defs>
                    <filter id='shadow'>
                      <feDropShadow
                        dx='2'
                        dy='2'
                        stdDeviation='4'
                        floodColor='#aaa'
                      />
                    </filter>
                  </defs>
                  <Tooltip />
                  <Pie
                    data={pieData}
                    dataKey='value'
                    outerRadius='80%'
                    labelLine={false}
                    label={({
                      cx,
                      cy,
                      midAngle,
                      innerRadius,
                      outerRadius,
                      percent,
                    }) => {
                      const RADIAN = Math.PI / 180
                      const radius =
                        innerRadius + (outerRadius - innerRadius) * 0.6
                      const x = cx + radius * Math.cos(-midAngle * RADIAN)
                      const y = cy + radius * Math.sin(-midAngle * RADIAN)

                      return (
                        <text
                          x={x}
                          y={y}
                          fill='#000'
                          textAnchor='middle'
                          dominantBaseline='central'
                          fontSize={14}
                          fontWeight='bold'>
                          {(percent * 100).toFixed(0)}%
                        </text>
                      )
                    }}
                    style={{ filter: 'url(#shadow)' }}>
                    {pieData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Pie>

                  <Legend
                    iconType='circle'
                    verticalAlign='bottom'
                    layout='horizontal'
                    wrapperStyle={{ fontSize: '15px', color: 'black' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}

            {!showPieChart && pieError && (
              <div className='bg-P-300 text-black font-semibold text-center px-4 py-3 rounded shadow'>
                {pieError}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
